import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CopyableText } from "components/CopyableText";
import { CredentialVerificationStatusChip } from "components/CredentialVerificationStatusChip";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormTextField } from "components/Forms/FormTextField";
import {
  CredentialForStaffInput,
  CredentialForStaffQuery,
  CredentialInputForValidation,
  CredentialKind,
  CredentialVerificationStatus,
  StaffCredentialPortalOptionsQuery,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Control, FormProvider, useWatch } from "react-hook-form";
import { credentialKindToLabel } from "util/constants";
import { prettyPrintDateTimeMed } from "util/dates";
import { hasCriticalIssues } from "util/forms";
import { getGraphQLErrorCode } from "utils/errorUtils";

import { useCredentialInputValidation } from "../../../Customer/Chemicals/Report/validationHooks/useCredentialInputValidation";
import { EditablePasswordField } from "../../../Customer/Credentials/EditablePasswordField";
import {
  CREDENTIAL_FOR_STAFF_QUERY,
  CREDENTIAL_ID_FOR_PORTAL_USERNAME,
  LINK_FACILITIES_TO_CREDENTIAL,
  STAFF_PORTAL_OPTIONS,
  UNLINK_FACILITIES_FROM_CREDENTIAL,
  UPSERT_CREDENTIAL_FOR_STAFF,
} from "./api";
import { ConfirmRemoveTenant } from "./ConfirmRemoveTenant";
import { SelectFacilitiesDialog } from "./SelectFacilitiesDialog";

type Credential = NonNullable<CredentialForStaffQuery["credentialForStaff"]>;
type Tenant = Credential["tenants"][number];
type FormValues = CredentialInputForValidation;

const KIND_ITEMS = Object.values(CredentialKind).map((kind) => ({
  value: kind,
  label: credentialKindToLabel(kind),
}));

// Matches `DUPLICATE_CREDENTIAL_ERROR_CODE` in `resolvers/upsertCredentialForStaff.ts`.
// A code, unlike a message, is the part of the error the server treats as a
// contract — the fulfillment activities grid keys off `REPORT_LOCKED` the same way.
const DUPLICATE_CREDENTIAL_ERROR_CODE = "DUPLICATE_CREDENTIAL";

/** Fallback copy only; the server's own message is preferred when present. */
const DUPLICATE_MESSAGE = "A credential with this username already exists for this portal.";

function listLabels(labels: string[]): string {
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

/**
 * A half-filled form produces one issue per field — "Name is required",
 * "Username is required" — which read badly strung together. Rewrite a set of
 * those as one sentence. Anything else is passed through untouched, since it
 * carries information this can't summarise.
 */
function friendlySaveError(message: string): string {
  if (!message) return "Something went wrong saving this credential.";

  const parts = message
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const labels = parts.map((part) => part.replace(/\s+is required\.?$/i, ""));
  const everyPartWasRequired = parts.every((part, index) => labels[index] !== part);

  if (!everyPartWasRequired || labels.length === 0) return message;
  return `Fill in ${listLabels(labels)} before saving.`;
}

const portalLabel = (portal: { description?: string | null; jurisdiction: string }) =>
  portal.description ?? portal.jurisdiction;

/**
 * Every field keeps `ErrorDisplay`'s 24px error slot reserved whether or not it has
 * a message, so errors appear into space that already exists and the dialog doesn't
 * resize. Collapsing the slot to 0 is what made it grow by ~20px per errored field.
 *
 * That reserved slot is also the gap between fields, so there's no extra margin.
 */
const FIELD_SPACING = {
  "& .MuiFormHelperText-root": { marginTop: 0, marginBottom: 0 },
} as const;

/** A form row: 56px input plus the 24px error slot below it. */
const FIELD_ROW_HEIGHT = 80;

/**
 * Read-only counterpart to a form field, sized to one so the right column's rows
 * land on the same lines as the left's.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.5} sx={{ minHeight: FIELD_ROW_HEIGHT }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * 2,672 facilities on the largest credential in prod, and no virtualisation here —
 * a row each froze the dialog. Counts and Remove tenant still use the full list.
 */
const MAX_FACILITY_ROWS = 25;

function TenantCard({
  tenant,
  onUnlink,
  onRemoveTenant,
  disabled,
}: {
  tenant: Tenant;
  onUnlink: (facilityIds: string[]) => void;
  onRemoveTenant: (tenant: Tenant, lastInTenant?: boolean) => void;
  disabled: boolean;
}) {
  // The only route to the remove button on facility 26 and beyond. Opt-in, because
  // a large group is slow to paint.
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? tenant.facilities : tenant.facilities.slice(0, MAX_FACILITY_ROWS);
  const hidden = tenant.facilities.length - shown.length;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={500}>
          {tenant.name} ({tenant.facilities.length})
        </Typography>
        {/* Confirmed, unlike the per-facility control: this drops the whole group. */}
        <Button
          color="error"
          size="small"
          disabled={disabled}
          onClick={() => onRemoveTenant(tenant)}
        >
          Remove tenant
        </Button>
      </Stack>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {shown.map((facility) => (
          <Stack
            key={facility.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              {facility.name}
            </Typography>
            <IconButton
              size="small"
              aria-label={`Unlink ${facility.name}`}
              disabled={disabled}
              // Confirmed only when it's the last one, because that quietly drops
              // the tenant from the credential as well.
              onClick={() =>
                tenant.facilities.length === 1
                  ? onRemoveTenant(tenant, true)
                  : onUnlink([facility.id])
              }
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {(hidden > 0 || expanded) && (
          <Button
            size="small"
            sx={{ alignSelf: "flex-start" }}
            onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
          >
            {expanded ? `Show fewer` : `Show ${hidden} more ${pluralize("facility", hidden)}`}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

function LinkedFacilities({
  credential,
  onAdd,
  onUnlink,
  disabled,
  busy,
}: {
  credential?: Credential;
  onAdd: () => void;
  onUnlink: (facilityIds: string[]) => Promise<void>;
  disabled: boolean;
  busy: boolean;
}) {
  const tenants = credential?.tenants ?? [];
  const tenantCount = credential?.tenantCount ?? 0;
  const facilityCount = credential?.facilityCount ?? 0;
  const [pending, setPending] = useState<{ tenant: Tenant; lastInTenant: boolean } | null>(null);

  return (
    <Box
      sx={{
        // grey[50] is near-white, so dark mode needs the inverse wash.
        bgcolor: (t) => (t.palette.mode === "dark" ? "action.hover" : grey[50]),
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            Linked facilities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`${tenantCount} ${pluralize("tenant", tenantCount)} • ${facilityCount} ${pluralize("facility", facilityCount)}`}
          </Typography>
        </Stack>
        <Button variant="outlined" startIcon={<Add />} disabled={disabled} onClick={onAdd}>
          Add facilities
        </Button>
      </Stack>
      <Stack spacing={2} sx={{ mt: 2, maxHeight: 360, overflowY: "auto" }}>
        {tenants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No facilities are linked to this credential.
          </Typography>
        ) : (
          tenants.map((tenant) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              onUnlink={onUnlink}
              onRemoveTenant={(target, lastInTenant = false) =>
                setPending({ tenant: target, lastInTenant })
              }
              disabled={disabled}
            />
          ))
        )}
      </Stack>
      {pending && (
        <ConfirmRemoveTenant
          tenantName={pending.tenant.name}
          facilities={pending.tenant.facilities}
          lastInTenant={pending.lastInTenant}
          loading={busy}
          onConfirm={async () => {
            await onUnlink(pending.tenant.facilities.map((facility) => facility.id));
            setPending(null);
          }}
          onClose={() => setPending(null)}
        />
      )}
    </Box>
  );
}

// Derived from the query rather than hand-written, so adding a field to the
// document can't leave this shape behind.
type PortalOption = StaffCredentialPortalOptionsQuery["portalPickerOptions"][number];

/**
 * Portal picker plus the selected portal's URL. The list is resolved once by the
 * dialog and passed in, so the picker and the subtitle can't disagree.
 */
function PortalField({
  control,
  portals,
  disabled,
}: {
  control: Control<FormValues>;
  portals: readonly PortalOption[];
  disabled?: boolean;
}) {
  const portalId = useWatch({ control, name: "portalId" });

  const url = portals.find((portal) => portal.id === portalId)?.url;

  const items = useMemo(
    () => portals.map((portal) => ({ value: portal.id, label: portalLabel(portal) })),
    [portals]
  );

  return (
    <FormAutocomplete
      name="portalId"
      label="Portal"
      control={control}
      disabled={disabled}
      required
      autocompleteItems={items}
      rules={{ required: true }}
      // In the error slot, which is reserved whether or not it's used, so the URL
      // costs no height and a portal error simply replaces it. As its own row it
      // added 28px and knocked every field below out of line with the left column.
      helperText={
        url ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Tooltip title={url}>
              {/* flex + minWidth:0 is what lets it ellipsize; without the zero floor
                  the URL's full width becomes the column's minimum. */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {url}
              </Box>
            </Tooltip>
            <Box sx={{ flexShrink: 0 }}>
              <CopyableText hideText>{url}</CopyableText>
            </Box>
          </Box>
        ) : undefined
      }
    />
  );
}

function CredentialFormFields({
  control,
  credential,
  mode,
  disabled,
  portals,
  onAddFacilities,
  onUnlinkFacilities,
  facilitiesBusy,
  passwordMode,
  passwordHelperText,
  showPasswordCopy,
  passwordValue,
  onPasswordEdit,
  onPasswordCancelEdit,
}: {
  control: Control<FormValues>;
  credential?: Credential;
  mode: "add" | "edit";
  disabled: boolean;
  portals: readonly PortalOption[];
  onAddFacilities: () => void;
  onUnlinkFacilities: (facilityIds: string[]) => Promise<void>;
  facilitiesBusy: boolean;
  passwordMode: "add" | "edit";
  passwordHelperText?: string;
  showPasswordCopy: boolean;
  passwordValue: string;
  onPasswordEdit: () => void;
  onPasswordCancelEdit: () => void;
}) {
  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={4} alignItems="stretch">
        {/* minWidth: 0 on both columns: a flex item's default `min-width: auto`
            lets long content — the portal URL — set a floor that pushes the
            divider and squeezes the other column. */}
        <Stack sx={{ flex: 1, minWidth: 0, ...FIELD_SPACING }}>
          <FormTextField
            name="name"
            label="Name"
            control={control}
            disabled={disabled}
            textFieldProps={{ required: true }}
          />
          <FormTextField
            name="username"
            label="Username"
            control={control}
            disabled={disabled}
            textFieldProps={{ required: true }}
          />
          <FormTextField name="email" label="Email" control={control} disabled={disabled} />
          {/* A direct child of the column, so it runs full width like the fields above.
              It used to share a row with a password-reset button that had no backing
              mutation anywhere, and no counterpart on the customer dialog. */}
          <EditablePasswordField
            name="password"
            label={
              <>
                {"Password"}
                {showPasswordCopy && <CopyableText hideText>{passwordValue}</CopyableText>}
              </>
            }
            mode={passwordMode}
            helperText={passwordHelperText}
            control={control}
            disabled={disabled}
            // Staff only reach this page, and they can already reveal any password on
            // the customer dialog, so the eye is always available.
            canViewCredentials
            onEditClick={onPasswordEdit}
            onCancelEdit={onPasswordCancelEdit}
            required
          />
          {/* Sits with the inputs rather than in the read-only column, which
              leaves the two sides roughly even. */}
          <Field label="Owner">
            <CheckboxField
              name="isEncampOwned"
              label="Encamp-owned"
              control={control}
              disabled={disabled}
              noErrorDisplay
            />
          </Field>
        </Stack>

        <Divider orientation="vertical" flexItem />

        {/* No spacing: every row on both sides is one `FIELD_ROW_HEIGHT`, so the
            columns stay in step without anything between them. */}
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Stack sx={FIELD_SPACING}>
            <FormAutocomplete
              name="kind"
              label="Type"
              control={control}
              disabled={disabled}
              required
              autocompleteItems={KIND_ITEMS}
              rules={{ required: true }}
            />
            <PortalField control={control} portals={portals} disabled={disabled} />
          </Stack>
          <Field label="Credential Status">
            <Box>
              <CredentialVerificationStatusChip
                status={credential?.verificationStatus ?? CredentialVerificationStatus.NotAttempted}
              />
            </Box>
          </Field>
          <Field label="Last verified">
            <Typography>
              {credential?.lastVerificationCheckAt
                ? prettyPrintDateTimeMed(credential.lastVerificationCheckAt)
                : "—"}
            </Typography>
          </Field>
          <Field label="Currently in use">
            {credential?.currentlyInUse ? <Check /> : <Typography>—</Typography>}
          </Field>
        </Stack>
      </Stack>

      <LinkedFacilities
        credential={credential}
        onAdd={onAddFacilities}
        onUnlink={onUnlinkFacilities}
        // Linking needs a saved credential to attach rows to.
        disabled={mode === "add" || facilitiesBusy}
        busy={facilitiesBusy}
      />
    </Stack>
  );
}

export function CredentialDetailDialog({
  credentialId,
  open,
  onClose,
  onOpenExisting,
}: {
  /** null while open means add mode. */
  credentialId: string | null;
  open: boolean;
  onClose: () => void;
  /** Jumps to the credential already holding a duplicate (portal, username). */
  onOpenExisting?: (id: string) => void;
}) {
  const alerts = useAlerts();
  const mode = credentialId ? "edit" : "add";

  const { data, loading } = useQuery(CREDENTIAL_FOR_STAFF_QUERY, {
    variables: { id: credentialId ?? "" },
    skip: !credentialId,
    fetchPolicy: "cache-and-network",
  });

  const credential = credentialId ? data?.credentialForStaff : undefined;

  const [upsert, { loading: saving }] = useMutation(UPSERT_CREDENTIAL_FOR_STAFF, {
    refetchQueries: ["CredentialsForStaff", "CredentialForStaff"],
  });
  const [lookupExisting] = useLazyQuery(CREDENTIAL_ID_FOR_PORTAL_USERNAME, {
    fetchPolicy: "no-cache",
  });

  const [duplicateExistingId, setDuplicateExistingId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // `EditablePasswordField` keeps its own copy of this, but doesn't expose it, and
  // the copy button in the label is outside the component — so the pencil's state
  // has to be mirrored here. Same reason the customer dialog tracks it.
  const [isEditingPassword, setIsEditingPassword] = useState(false);

  // Both mutations return the credential's refreshed tenant groups, so the
  // modal's Linked Facilities block updates without a manual refetch.
  const [linkFacilities, { loading: linking }] = useMutation(LINK_FACILITIES_TO_CREDENTIAL, {
    refetchQueries: ["CredentialsForStaff"],
  });
  const [unlinkFacilities, { loading: unlinking }] = useMutation(
    UNLINK_FACILITIES_FROM_CREDENTIAL,
    { refetchQueries: ["CredentialsForStaff"] }
  );

  const linkedFacilityIds = useMemo(
    () =>
      new Set(
        (credential?.tenants ?? []).flatMap((tenant) =>
          tenant.facilities.map((facility) => facility.id)
        )
      ),
    [credential]
  );

  const handleAddFacilities = useCallback(
    async (facilityIds: string[]) => {
      if (!credentialId) return;
      try {
        await linkFacilities({ variables: { credentialId, facilityIds } });
        setPickerOpen(false);
        alerts.success(`${facilityIds.length} ${pluralize("facility", facilityIds.length)} linked`);
      } catch (error) {
        alerts.error(error instanceof Error ? error.message : "Could not link facilities");
      }
    },
    [alerts, credentialId, linkFacilities]
  );

  const handleUnlinkFacilities = useCallback(
    async (facilityIds: string[]) => {
      if (!credentialId) return;
      try {
        await unlinkFacilities({ variables: { credentialId, facilityIds } });
        alerts.success(
          `${facilityIds.length} ${pluralize("facility", facilityIds.length)} unlinked`
        );
      } catch (error) {
        alerts.error(error instanceof Error ? error.message : "Could not unlink facilities");
      }
    },
    [alerts, credentialId, unlinkFacilities]
  );

  const defaultValues: FormValues = useMemo(
    () => ({
      id: credential?.id,
      name: credential?.name ?? "",
      username: credential?.username ?? "",
      email: credential?.email ?? "",
      password: credential?.password ?? "",
      kind: credential?.kind ?? CredentialKind.Tierii,
      portalId: credential?.portalId ?? null,
      isEncampOwned: credential?.isEncampOwned ?? false,
    }),
    [credential]
  );

  /**
   * The field holds the stored password so the eye has something to reveal, so
   * "changed?" is a comparison against it — reading the pencil would be wrong for
   * both cancel and retype-the-same. An unchanged edit sends "", which
   * `credentialInputValidator` substitutes the stored value for when an `id` is
   * passed. That keeps the plaintext off the wire on every debounced validation and
   * stops an unrelated edit re-encrypting a secret that never changed.
   */
  const passwordForServer = useCallback(
    (typed: string | null | undefined) => {
      const value = typed ?? "";
      if (mode === "add") return value;
      return value === (credential?.password ?? "") ? "" : value;
    },
    [mode, credential]
  );

  /**
   * Whether there is a password to keep behind the pencil at all. False when one
   * was never set, and also when the stored ciphertext can't be read — `decrypt`
   * throwing is indistinguishable from an empty column by the time it reaches here,
   * which is what every seeded sandbox credential looks like.
   *
   * When there is none the field takes its add-mode shape: open for typing, no
   * pencil to click, and — the reason this exists — no `*********` placeholder.
   * That placeholder is hardcoded inside `EditablePasswordField` and only rendered
   * for `mode === "edit"`, so a caller cannot override it; it sat over an empty
   * input claiming a password was there, and the eye had nothing to reveal.
   */
  const hasStoredPassword = Boolean(credential?.password);
  const passwordMode = hasStoredPassword ? mode : "add";
  const passwordHelperText =
    mode === "edit" && !hasStoredPassword
      ? "No password available — enter one to set it."
      : undefined;

  const validateCredentialInput = useCredentialInputValidation(mode === "add");

  // Wrapped rather than passed straight through: the resolver runs on a 500ms
  // debounce as the user types, and the raw form still holds the stored plaintext
  // for the reveal control.
  const validateInput = useCallback(
    (values: FormValues) =>
      validateCredentialInput({ ...values, password: passwordForServer(values.password) }),
    [validateCredentialInput, passwordForServer]
  );

  const form = useValidatingForm<FormValues>(defaultValues, [], validateInput, undefined, {
    keepDirtyValuesOnReset: true,
  });
  const { control, getValues, setValue, trigger, handleSubmit, issues, formState } = form;

  const watchedKind = useWatch({ control, name: "kind" });
  const previousKind = useRef(watchedKind);

  /**
   * The portal list for the picker.
   *
   * Filtered by `kind` so a portal whose type disagrees isn't proposed for new
   * work; that filter is what actually keeps the pairing clean, since the
   * validation rule behind it is ERROR-level and never blocks a write.
   * `includeId` exempts the portal a credential already uses from both that
   * filter and the active-only one — the kind→type map is narrower than the data
   * (a `TIERII` credential on an `LEPC` portal is ordinary) and a portal can be
   * deactivated after first use. The exemption lapses as soon as the type is
   * changed, because that's a deliberate re-categorisation.
   */
  const { data: portalData } = useQuery(STAFF_PORTAL_OPTIONS, {
    variables: {
      kind: watchedKind ?? undefined,
      includeId: watchedKind === credential?.kind ? (credential?.portalId ?? undefined) : undefined,
    },
  });
  const portals = useMemo(() => portalData?.portalPickerOptions ?? [], [portalData]);

  const subtitle = useMemo(() => {
    if (!credential) return "";
    const { tenantCount } = credential;
    // "Shared between 0 tenants" reads as a bug rather than as a credential nothing
    // is linked to yet.
    return tenantCount === 0
      ? "No linked facilities"
      : `Shared between ${tenantCount} ${pluralize("tenant", tenantCount)}`;
  }, [credential]);

  // A portal only belongs to one kind, so changing the kind invalidates the choice.
  // `shouldDirty` or the on-focus refetch puts the old portal back under the new
  // Type — only `keepDirtyValuesOnReset` fields survive a reset, and that mismatched
  // pairing is ERROR-level, so it would save.
  // Recorded before the dirty guard, not after: the ref is seeded on the first
  // render, when an edit is still showing the add-form default, so the load itself
  // changes the kind. Updating after the guard left that change pending and the
  // first edit to any field read as a Type change and blanked a good portal.
  useEffect(() => {
    const kindChanged = previousKind.current !== watchedKind;
    previousKind.current = watchedKind;
    if (!kindChanged || !formState.isDirty) return;
    setValue("portalId", null, { shouldDirty: true });
  }, [watchedKind, setValue, formState.isDirty]);

  useEffect(() => {
    setDuplicateExistingId(null);
    setIsEditingPassword(false);
  }, [credentialId, open]);

  // Clearing is what makes the pencil mean "replace this". Guarded because
  // `EditablePasswordField` also wires this to `onFocus`, so clicking away from a
  // half-typed replacement and back would otherwise wipe it.
  const onPasswordEdit = useCallback(() => {
    if (passwordMode !== "edit" || isEditingPassword) return;
    setIsEditingPassword(true);
    setValue("password", "", { shouldDirty: true });
  }, [passwordMode, isEditingPassword, setValue]);

  const onPasswordCancelEdit = useCallback(() => {
    setIsEditingPassword(false);
    setValue("password", credential?.password ?? "", { shouldDirty: true });
  }, [setValue, credential]);

  /**
   * The unique index spans every tenant, so the credential blocking this save may be
   * one the user can't otherwise find — look it up and offer to open it. Keyed on the
   * error code, not the message, which an English tweak would silently break.
   */
  const handleSaveError = useCallback(
    async (error: unknown) => {
      const message = error instanceof Error ? error.message : "";
      if (getGraphQLErrorCode(error) !== DUPLICATE_CREDENTIAL_ERROR_CODE) {
        alerts.error(friendlySaveError(message));
        return;
      }

      alerts.error(message || DUPLICATE_MESSAGE);
      const portalId = getValues("portalId");
      const username = getValues("username");
      if (!portalId || !username) return;

      const { data: lookup } = await lookupExisting({ variables: { portalId, username } });
      setDuplicateExistingId(lookup?.credentialIdForPortalUsername ?? null);
    },
    [alerts, getValues, lookupExisting]
  );

  const onSubmit = async () => {
    const values = { ...getValues(), password: passwordForServer(getValues("password")) };

    // Awaited directly rather than read off `issues`, which is 500ms-debounced and a
    // fast click outruns. `trigger()` paints the outlines: `useValidatingForm` owns
    // field errors, so a direct `setError` is wiped by its next effect.
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      alerts.error(friendlySaveError(submitIssues.map((issue) => issue.message).join(",")));
      void trigger();
      return;
    }

    // Annotated: excess-property checking only fires on a literal assigned to a typed
    // target, and this is passed on as `variables: { input }`. Unannotated, a
    // misspelled key compiled clean and never reached the server.
    const input: CredentialForStaffInput = {
      name: values.name ?? "",
      username: values.username ?? "",
      kind: values.kind ?? CredentialKind.Tierii,
      email: values.email,
      portalId: values.portalId,
      isEncampOwned: values.isEncampOwned,
      password: values.password || undefined,
    };

    try {
      await upsert({ variables: { id: credentialId, input } });
      alerts.success(mode === "add" ? "Credential created" : "Credential saved");
      onClose();
    } catch (error) {
      await handleSaveError(error);
    }
  };

  const saveDisabled = saving || hasCriticalIssues(issues);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={
        <Stack spacing={0.25}>
          <Typography variant="h6">
            {mode === "add" ? "Add Credential" : "Edit Credential"}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
      }
      primaryActionButtonProps={{
        saveText: mode === "add" ? "Create credential" : "Save changes",
        loading: saving,
        disabled: saveDisabled,
        onClick: handleSubmit(onSubmit),
      }}
    >
      {loading && !credential && mode === "edit" && <Skeleton variant="rounded" height={420} />}
      {!loading && !credential && mode === "edit" && (
        <Typography color="text.secondary">Credential not found.</Typography>
      )}
      {(mode === "add" || credential) && (
        <FormProvider {...form}>
          {mode === "add" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              New credentials are currently not assigned to a single tenant, and so customers will
              not be able to see them.
            </Alert>
          )}
          {duplicateExistingId && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => onOpenExisting?.(duplicateExistingId)}
                >
                  Open it
                </Button>
              }
            >
              That portal and username are already taken by another credential.
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <CredentialFormFields
              control={control}
              credential={credential ?? undefined}
              mode={mode}
              disabled={saving}
              portals={portals}
              onAddFacilities={() => setPickerOpen(true)}
              onUnlinkFacilities={handleUnlinkFacilities}
              facilitiesBusy={linking || unlinking}
              passwordMode={passwordMode}
              passwordHelperText={passwordHelperText}
              // Hidden while the pencil is active: the copy button offers the
              // stored password, which is no longer what the field shows.
              showPasswordCopy={passwordMode === "edit" && !isEditingPassword}
              passwordValue={credential?.password ?? ""}
              onPasswordEdit={onPasswordEdit}
              onPasswordCancelEdit={onPasswordCancelEdit}
            />
          </Box>
        </FormProvider>
      )}
      {/* Mounted only while open: kept mounted it held its search, tenant filter and
          selection across closes, offering a stale selection as though it were fresh. */}
      {pickerOpen && (
        <SelectFacilitiesDialog
          open
          linkedFacilityIds={linkedFacilityIds}
          saving={linking}
          onSave={handleAddFacilities}
          onCancel={() => setPickerOpen(false)}
        />
      )}
    </Dialog>
  );
}
